import TileLayer from 'ol/layer/WebGLTile'
import { Extent } from 'ol/extent'
import { transformExtent } from 'ol/proj'
import GeoTIFF from 'ol/source/GeoTIFF'
import { useEffect, useRef } from 'react'
import useMapStore from 'store/map'

interface ApriorMapLayerProps {
  url?: string | null
  ovrUrl?: string | null
  visible?: boolean
  opacity?: number
  onExtentChange?: (extent: Extent | null, centerOnLoad: boolean) => void
  onError?: () => void
  centerOnLoad?: boolean
}

// Read-only GeoTIFF layer of the selected a priori map version.
// Both file URLs come from the API; this component does not construct storage paths.
// Display settings are local only and never persisted to the server.
// The layer intentionally has no `showInLayerList` property,
// so it never appears in the map layers control.
const ApriorMapLayer = ({
  url,
  ovrUrl,
  visible = true,
  opacity = 1,
  onExtentChange,
  onError,
  centerOnLoad = false,
}: ApriorMapLayerProps) => {
  const map = useMapStore(s => s.map)
  const layerRef = useRef<TileLayer | null>(null)
  const onExtentChangeRef = useRef(onExtentChange)
  onExtentChangeRef.current = onExtentChange
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  useEffect(() => {
    onExtentChangeRef.current?.(null, false)
    if (!map || !url) return

    let cancelled = false
    let source: GeoTIFF | null = null
    const onTileError = () => {
      if (!cancelled) onErrorRef.current?.()
    }
    try {
      const geoTiffSource = new GeoTIFF({
        sources: [{ url, ...(ovrUrl ? { overviews: [ovrUrl] } : {}) }],
      })
      source = geoTiffSource
      geoTiffSource.on('tileloaderror', onTileError)
      const layer = new TileLayer({
        source: geoTiffSource,
        opacity,
        visible,
        // Base map lives at zIndex 0. Order by zIndex instead of insertAt(index):
        // the base layer is recreated on satellite toggle, so a fixed index is fragile.
        zIndex: 1,
      })
      if (!cancelled) {
        map.addLayer(layer)
        layerRef.current = layer
      }
      geoTiffSource
        .getView()
        .then(view => {
          if (cancelled || !view.extent) return
          const projection = view.projection || geoTiffSource.getProjection() || map.getView().getProjection()
          onExtentChangeRef.current?.(
            transformExtent(view.extent, projection, map.getView().getProjection()),
            centerOnLoad
          )
        })
        .catch(e => {
          if (cancelled) return
          onErrorRef.current?.()
          // eslint-disable-next-line no-console
          console.error('Failed to load apriori map tif', e)
        })
    } catch (e) {
      onErrorRef.current?.()
      // eslint-disable-next-line no-console
      console.error('Failed to load apriori map tif', e)
    }

    return () => {
      cancelled = true
      source?.un('tileloaderror', onTileError)
      onExtentChangeRef.current?.(null, false)
      if (layerRef.current) {
        map.removeLayer(layerRef.current)
        layerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, ovrUrl, map])

  useEffect(() => {
    if (layerRef.current) {
      layerRef.current.setVisible(visible)
      layerRef.current.setOpacity(opacity)
    }
  }, [visible, opacity])

  return null
}

export default ApriorMapLayer
